import { attendanceCoordinator, AttendanceOperation, AttendanceResult, AttendanceScope, AttendanceSubmission } from '../hooks/useAttendanceCapture.ts';
import { parseApiDateTime } from './parseApiDateTime';
import { withStrictPhoneCheck } from './leadPhone';
import { AttendanceTarget } from '../components/attendance/attendanceCapture.ts';
import { attendanceUiEnabledFor } from './attendanceUiFlag';

// Resolve legacy API/store modules only after application initialization.
const callApi = async (...args: any[]) => { const api = await import('./apiCall'); return api.callApi(...args); };

export const isSeparatelyRequestedFutureTreatmentBooking = (appointment: any) => {
    const date = appointment?.type === 'TREATMENT' ? parseApiDateTime(appointment.datetime) : null;
    return Boolean(date?.isValid() && date.valueOf() > Date.now());
};

type Response = { status: number; data: any; ok?: boolean };
type TreatmentAction = { practiceId: number; leadId: number; treatmentId: number; targetStatus: Exclude<AttendanceTarget, null>; appointmentId?: number; extraData?: Record<string, unknown>; taskTransition?: boolean; attendanceReceipt?: boolean; preparation?: { url: string; method: string; data: Record<string, unknown> } };
const active = new Set<string>();
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
const sameScope = (left: AttendanceScope | null, right: AttendanceScope | null) => Boolean(left && right
    && String(left.practiceId) === String(right.practiceId) && left.epoch === right.epoch && left.sessionKey === right.sessionKey);
const readable = (value: any): string => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(readable).filter(Boolean).join(' ');
    if (value && typeof value === 'object') return value.detail ? readable(value.detail) : Object.values(value).map(readable).filter(Boolean).join(' ');
    return '';
};
export const attendanceResponse = (response: Response) => {
    if (!response || response.status < 200 || response.status >= 300) {
        throw Object.assign(new Error(readable(response?.data) || 'The change could not be confirmed. Please retry.'), { status: response?.status || 0, code: response?.data?.code });
    }
    return response;
};
const scopeFor = (practiceId: number) => {
    const scope = attendanceCoordinator.getScope();
    if (!scope || String(scope.practiceId) !== String(practiceId)) throw new Error('Attendance is unavailable for this practice. Close and reopen the action.');
    return scope;
};
const actionBody = (submission: AttendanceSubmission, extra: Record<string, unknown> = {}) => ({
    ...extra, request_id: submission.request_id,
    ...(submission.decision ? { attendance_decision: submission.decision } : { treatment_updated_at: submission.treatment_updated_at }),
});
const invoke = async (key: string, operation: AttendanceOperation): Promise<AttendanceResult> => {
    // A second caller must not receive the first caller's success continuation.
    if (active.has(key)) return { status: 'cancelled' };
    active.add(key);
    try {
        const result = await attendanceCoordinator.request(operation);
        return sameScope(operation.scope, attendanceCoordinator.getScope()) ? result : { status: 'cancelled' };
    } finally { active.delete(key); }
};
// Legacy requests (practice without attendance capture) are exactly what production
// sent before capture existed. They must NOT depend on the coordinator scope: that
// scope exists for the dialog flow, and requiring it here broke production on
// 2026-10-28 ('Attendance is unavailable for this practice').
// The consultation-notes dialog (appointmentThunks.attenedAppoimentNotes with
// attendanceReceipt) continues from an attendance RECEIPT
// {practice_id, lead_id, treatment_id, status, appointment_id}. The legacy
// endpoints answer with the LeadTreatment itself, so a receipt is derived from
// it: the attended consultation on the treatment, or null when there is none.
// Without this the dialog threw 'Treatment started, but its confirmed
// consultation could not be verified. Your notes have not been saved.' for every
// practice (Kingston, 2026-10-29) — the treatment had started, the notes were lost.
export const legacyReceiptFromTreatment = (treatment: any, { practiceId, leadId, treatmentId, targetStatus }: { practiceId: number; leadId: number; treatmentId: number; targetStatus: string }) => {
    const appointments: any[] = Array.isArray(treatment?.appointments) ? treatment.appointments : [];
    const attended = appointments.find((a) => a && a.type === 'CONSULTATION' && a.status === 'ATTENDED');
    const lead = treatment?.lead;
    return {
        practice_id: practiceId,
        lead_id: lead && typeof lead === 'object' ? (lead.id ?? leadId) : (lead ?? leadId),
        treatment_id: treatment?.id ?? treatmentId,
        status: treatment?.status ?? targetStatus,
        appointment_id: attended ? attended.id : null,
        legacy: true,
    };
};

const legacyTreatmentAction = async ({ practiceId, leadId, treatmentId, targetStatus, url, extra, before }: { practiceId: number; leadId: number; treatmentId: number; targetStatus: string; url: string; extra: Record<string, unknown>; before: { url: string; method: string; data?: unknown } | null }): Promise<AttendanceResult> => {
    if (before) attendanceResponse(await callApi(before.url, before.method, before.data));
    const result = attendanceResponse(await callApi(url, 'post', extra));
    return { status: 'success', data: result, receipt: legacyReceiptFromTreatment(result?.data, { practiceId, leadId, treatmentId, targetStatus }) };
};
export function captureTreatmentAction({ practiceId, leadId, treatmentId, targetStatus, appointmentId, extraData = {}, taskTransition = false, preparation, attendanceReceipt = false }: TreatmentAction) {
    if (!leadId || !treatmentId) return Promise.reject(new Error('Select the exact treatment before recording attendance.'));
    const extra = clone({ ...extraData, ...(taskTransition ? { target_status: targetStatus } : {}) });
    const before = preparation ? clone(preparation) : null;
    const preparedBodies = new Map<string, Record<string, unknown>>();
    const prepared = new Set<string>();
    const action = targetStatus === 'TREATMENT_STARTED' ? 'treatment_started' : 'consultation_attended';
    const url = taskTransition ? `/api/v1/practices/${practiceId}/lead-treatments/${treatmentId}/transition/`
        : `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/${action}/`;
    if (!attendanceUiEnabledFor(practiceId)) {
        // Practice without attendance capture (PracticePreferences
        // .attendance_capture_eligible false): the backend takes the legacy body
        // (#7084), so send exactly what production sends today — no context load,
        // no pop-up. `preparation` (the treatment edit before a start) still runs first.
        return legacyTreatmentAction({ practiceId, leadId, treatmentId, targetStatus, url, extra, before });
    }
    const scope = scopeFor(practiceId);
    return invoke(`${practiceId}:${leadId}:${treatmentId}:${targetStatus}:${appointmentId || ''}`, {
        scope, leadId, treatmentId, targetStatus, appointmentId, requireTreatmentDecision: attendanceReceipt,
        submit: async (submission) => {
            let body = preparedBodies.get(submission.request_id);
            if (!body) {
                let version = submission.treatment_updated_at;
                if (before) {
                    if (!prepared.has(submission.request_id)) {
                        attendanceResponse(await callApi(before.url, before.method, before.data));
                        prepared.add(submission.request_id);
                    }
                    if (!sameScope(scope, attendanceCoordinator.getScope())) throw Object.assign(new Error('Practice changed before the treatment start.'), { status: 400 });
                    // Treatment serializers emit a legacy wall-time literal Z.
                    // Only attendance context supplies the canonical ISO version.
                    const query = new URLSearchParams({ target_status: targetStatus });
                    if (appointmentId) query.set('appointment_id', String(appointmentId));
                    const refreshed = attendanceResponse(await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/attendance-context/?${query}`, 'GET')).data;
                    if (!sameScope(scope, attendanceCoordinator.getScope())) throw Object.assign(new Error('Practice changed before the treatment start.'), { status: 400 });
                    version = refreshed?.treatment_updated_at;
                    if (String(refreshed?.practice_id) !== String(practiceId) || String(refreshed?.lead_id) !== String(leadId)
                        || String(refreshed?.treatment_id) !== String(treatmentId) || refreshed?.target_status !== targetStatus
                        || String(refreshed?.selected_appointment_id ?? null) !== String(appointmentId ?? null)
                        || typeof version !== 'string' || !/^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/.test(version) || !Number.isFinite(Date.parse(version))) {
                        throw Object.assign(new Error('Could not verify the prepared treatment version. Reload attendance details.'), { status: 409 });
                    }
                }
                const confirmed = { ...submission, treatment_updated_at: version,
                    decision: submission.decision && version ? { ...submission.decision, treatment_updated_at: version } : submission.decision };
                body = actionBody(confirmed, extra);
                preparedBodies.set(submission.request_id, body);
            }
            if (attendanceReceipt) {
                if (!body.attendance_decision) throw Object.assign(new Error('Confirm the consultation before starting treatment.'), { status: 400 });
                const result = attendanceResponse(await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/attendance-resolution/`, 'post', {
                    request_id: body.request_id, target_status: targetStatus, decision: body.attendance_decision,
                }));
                const receipt = result.data;
                if (String(receipt?.practice_id) !== String(practiceId) || String(receipt?.lead_id) !== String(leadId)
                    || String(receipt?.treatment_id) !== String(treatmentId) || receipt?.status !== targetStatus
                    || ((body.attendance_decision as any).kind === 'not_required' ? receipt.appointment_id !== null : receipt.appointment_id == null)
                    || (receipt.appointment_id !== null && (!Number.isSafeInteger(Number(receipt.appointment_id)) || Number(receipt.appointment_id) <= 0))) {
                    throw new Error('Treatment may have started, but its confirmed consultation could not be verified. Your notes have not been saved.');
                }
                return result;
            }
            return attendanceResponse(await callApi(url, 'post', body));
        },
    });
}
export async function captureAppointmentAttendance({ practiceId, leadId, appointmentId, legacyData = {} }: {
    practiceId: number; leadId: number; appointmentId: number; legacyData?: Record<string, unknown>;
}): Promise<AttendanceResult> {
    if (!leadId || !appointmentId) throw new Error('Select the exact appointment before recording attendance.');
    const legacy = !attendanceUiEnabledFor(practiceId);
    const scope = legacy ? null : scopeFor(practiceId);
    const key = `${practiceId}:${leadId}:appointment:${appointmentId}`;
    if (active.has(key)) return { status: 'cancelled' };
    active.add(key);
    try {
        const base = `/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${appointmentId}/`;
        const response = attendanceResponse(await callApi(base, 'GET'));
        if (!legacy && !sameScope(scope, attendanceCoordinator.getScope())) return { status: 'cancelled' };
        const appointment = response.data;
        const treatmentId = typeof appointment?.lead_treatment === 'object' ? appointment.lead_treatment?.id : appointment?.lead_treatment;
        if (String(appointment?.id) !== String(appointmentId) || !treatmentId || !['CONSULTATION', 'TREATMENT'].includes(appointment?.type)) {
            throw new Error('The selected appointment is unavailable. Close and reload the action.');
        }
        const validateOutcome = (result: Response) => {
            const row = result.data;
            const returnedTreatment = typeof row?.lead_treatment === 'object' ? row.lead_treatment?.id : row?.lead_treatment;
            const returnedLead = typeof row?.lead === 'object' ? row.lead?.id : row?.lead;
            if (!Number.isSafeInteger(Number(row?.id)) || Number(row.id) <= 0 || String(returnedTreatment) !== String(treatmentId)
                || row.type !== appointment.type || row.status !== 'ATTENDED' || (returnedLead != null && String(returnedLead) !== String(leadId))
                || (appointment.status === 'BOOKED' && String(row.id) !== String(appointmentId))) {
                throw new Error('Attendance may have saved, but its returned appointment does not match this patient and treatment. Retry the original confirmation or refresh.');
            }
            return result;
        };
        if (appointment.type === 'TREATMENT' || legacy) {
            // Treatment visits, and every visit for a practice without attendance
            // capture: the plain POST attended/ production has always sent.
            const result = validateOutcome(attendanceResponse(await callApi(`${base}attended/`, 'post', clone(legacyData))));
            if (legacy) return { status: 'success', data: result };
            return sameScope(scope, attendanceCoordinator.getScope()) ? { status: 'success', data: result } : { status: 'cancelled' };
        }
        return await invoke(`${key}:consultation`, {
            scope, leadId, treatmentId, appointmentId, targetStatus: 'CONSULTATION_ATTENDED', requireExplicitDecision: true,
            submit: async (submission) => validateOutcome(attendanceResponse(await callApi(`${base}attended/`, 'post', actionBody(submission)))),
        });
    } finally { active.delete(key); }
}

export function checklistTreatmentId({ item, leadData }: { item?: any; leadData?: any }): number | null {
    const idOf = (value: any) => value && typeof value === 'object' ? value.id : value;
    const candidates = [item?.rawTask?.lead_treatment_id, idOf(item?.rawTask?.lead_treatment), item?.lead_treatment_id,
        idOf(item?.lead_treatment), item?.raw?.lead_treatment_id, idOf(item?.raw?.lead_treatment),
        leadData?.treatment_id, idOf(leadData?.lead_treatment), idOf(leadData?.originalData?.lead_treatment)]
        .filter((id) => id !== undefined && id !== null && id !== '').map(Number);
    const unique = [...new Set(candidates)];
    return unique.length === 1 && Number.isSafeInteger(unique[0]) && unique[0] > 0 ? unique[0] : null;
}
export async function captureChecklistTransition({ practiceId, leadId, treatmentId, targetStatus }: {
    practiceId: number; leadId: number; treatmentId: number; targetStatus: string;
}): Promise<AttendanceResult> {
    if (!treatmentId || !leadId) throw new Error('The selected treatment is unavailable. Close and reload the checklist.');
    if (targetStatus === 'TREATMENT_STARTED' || targetStatus === 'CONSULTATION_ATTENDED') {
        return captureTreatmentAction({ practiceId, leadId, treatmentId, targetStatus, taskTransition: true });
    }
    const scope = scopeFor(practiceId);
    const response = attendanceResponse(await callApi(`/api/v1/practices/${practiceId}/lead-treatments/${treatmentId}/transition/`, 'POST', { target_status: targetStatus }));
    return sameScope(scope, attendanceCoordinator.getScope()) ? { status: 'success', data: response } : { status: 'cancelled' };
}

export function captureAttendanceResolution({ practiceId, leadId, treatmentId, appointmentId }: {
    practiceId: number; leadId: number; treatmentId: number; appointmentId?: number;
}): Promise<AttendanceResult> {
    const scope = scopeFor(practiceId);
    if (!leadId || !treatmentId) return Promise.reject(new Error('Select the exact treatment before resolving attendance.'));
    return invoke(`${practiceId}:${leadId}:${treatmentId}:review:${appointmentId || ''}`, {
        scope, leadId, treatmentId, targetStatus: null, appointmentId,
        submit: async (submission) => attendanceResponse(await callApi(
            `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/attendance-resolution/`, 'post',
            { request_id: submission.request_id, target_status: null, decision: submission.decision },
        )),
    });
}


export function captureAdvancedLeadCreate({ practiceId, operationKey, leadPayload, treatment, targetStatus, decision, identityResolution = null }: {
    practiceId: number; operationKey: string; leadPayload: Record<string, any>; treatment: Record<string, unknown>;
    targetStatus: Exclude<AttendanceTarget, null>; decision: AttendanceSubmission['decision']; identityResolution?: any;
}): Promise<AttendanceResult> {
    const scope = scopeFor(practiceId);
    if (!operationKey || !treatment?.treatment || !decision || !['attended', 'not_required'].includes(decision.kind)
        || (decision.kind === 'attended' && (!decision.occurred_at || !decision.practitioner_id))
        || (decision.kind === 'not_required' && (!decision.reason?.trim() || targetStatus === 'CONSULTATION_ATTENDED'))) {
        return Promise.reject(new Error('Complete the consultation date, dentist or required reason before creating the patient.'));
    }
    const parent = clone(leadPayload); delete parent.lead_treatments; delete parent.attendance_capture;
    const selectedTreatment = clone(treatment);
    const initialDecision = clone(decision);
    const initialIdentity = clone(identityResolution);
    let lastPosted: Record<string, any> | null = null;
    let lastRequestId: string | null = null;
    const options = { timeout: 30000, suppressServerErrorDispatch: true };
    return invoke(`${practiceId}:create:${operationKey}`, {
        scope, leadId: null, treatmentId: null, targetStatus, operationKey, initialDecision,
        label: `${[parent.first_name, parent.last_name].filter(Boolean).join(' ') || 'Original patient'} — ${selectedTreatment.treatment}`,
        submit: async (submission) => {
            if (lastPosted && lastRequestId === submission.request_id) {
                // Includes the identity choice actually submitted before a lost
                // response. Re-running identity selection would change its receipt.
                return attendanceResponse(await callApi(withStrictPhoneCheck(`/api/v1/practices/${practiceId}/leads/`), 'post', clone(lastPosted), false, options));
            }
            const capture = { request_id: submission.request_id, target_status: targetStatus, decision: submission.decision, treatment: selectedTreatment };
            const identity = lastPosted?.identity_resolution || initialIdentity;
            const { postManualLead } = await import('./leadIdentityApi');
            const outcome = await postManualLead(practiceId, { ...parent, attendance_capture: capture }, { ...options, strictPhone: true }, identity, (body: Record<string, any>) => {
                if (!sameScope(scope, attendanceCoordinator.getScope())) throw Object.assign(new Error('Practice or session changed before patient creation. No further request was sent.'), { status: 400, cancelled: true });
                lastPosted = clone(body); lastRequestId = submission.request_id;
            });
            if (outcome.cancelled) throw Object.assign(new Error('Patient creation cancelled.'), { status: 400, cancelled: true });
            return attendanceResponse(outcome.response);
        },
    });
}

// Recovery uses the retained operation, never a newly opened/cleared form.
export async function resumeAttendanceCreation() {
    const scope = attendanceCoordinator.getScope();
    const pending = attendanceCoordinator.getRecoverableCreation();
    if (!scope || !pending) return { status: 'cancelled' } as AttendanceResult;
    const result = await attendanceCoordinator.resumeCreation(pending.operationKey);
    if (result.status !== 'success' || !sameScope(scope, attendanceCoordinator.getScope())) return { status: 'cancelled' } as AttendanceResult;
    const response = result.data as Response;
    const [{ dispatch, store }, { leadActions }, { closeCreateLeadDialog }, { updateError }, { invalidateAttendance }] = await Promise.all([
        import('../redux/store'), import('../redux/slices/lead/slice'), import('../redux/slices/createLeadDialog'),
        import('../redux/slices/user'), import('../redux/slices/attendanceReview.ts'),
    ]);
    if (!sameScope(scope, attendanceCoordinator.getScope())) return { status: 'cancelled' } as AttendanceResult;
    const rows = store.getState().lead?.lead;
    if (rows && !rows.results?.some((row: any) => row.id === response.data.id)) dispatch(leadActions.updateLead({ results: [response.data, ...(rows.results || [])], count: (rows.count || 0) + 1 }));
    dispatch(leadActions.clearLead()); dispatch(closeCreateLeadDialog());
    dispatch(invalidateAttendance({ practiceId: scope.practiceId, leadId: response.data.id, treatmentId: response.data.attendance_capture?.treatment_id }));
    dispatch(updateError({ success: true, message: 'Original patient creation confirmed.', lead: response.data.id }));
    return result;
}

// Treatment-level attendance has no implicitly chosen appointment. Context
// prompts for the visit; the focused response supplies its authoritative ID.
export async function captureConsultationAttendance({ practiceId, leadId, treatmentId }: { practiceId: number; leadId: number; treatmentId: number }): Promise<AttendanceResult> {
    if (!leadId || !treatmentId) return Promise.reject(new Error('Select the exact treatment before recording attendance.'));
    const base = `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/`;
    if (!attendanceUiEnabledFor(practiceId)) {
        // Legacy path for a practice without attendance capture: the plain
        // consultation_attended/ POST production sends today, no pop-up, no scope.
        // Send {} not a missing body: callApi sends that as JSON null, which the
        // backend 500'd on until 9e9861b31 (Sentry JAVASCRIPT-REACT-1G4, 2026-10-28).
        const result = attendanceResponse(await callApi(`${base}consultation_attended/`, 'post', {}));
        const row = result.data;
        const appointmentId = row?.appointment_id ?? row?.appointments?.find?.((a: any) => a?.status === 'ATTENDED')?.id ?? null;
        return { status: 'success', data: { ...result, data: { appointment_id: appointmentId, treatment_id: treatmentId } } };
    }
    const scope = scopeFor(practiceId);
    return invoke(`${practiceId}:${leadId}:${treatmentId}:consultation-choice`, {
        scope, leadId, treatmentId, targetStatus: 'CONSULTATION_ATTENDED',
        submit: async submission => {
            if (submission.decision) return attendanceResponse(await callApi(`${base}attendance-resolution/`, 'post', {
                request_id: submission.request_id, target_status: 'CONSULTATION_ATTENDED', decision: submission.decision,
            }));
            const appointmentId = attendanceCoordinator.getSnapshot().context?.existing_attendance?.id;
            if (!appointmentId) throw Object.assign(new Error('Reload the attendance details to select the consultation.'), { status: 400, code: 'attendance_confirmation_required' });
            const result = attendanceResponse(await callApi(`${base}consultation_attended/`, 'post', actionBody(submission)));
            return { ...result, data: { appointment_id: appointmentId, treatment_id: treatmentId } };
        },
    });
}
